// 顧問先の追加・編集ダイアログ
import { useState } from 'react'
import * as api from '../api'
import { X, AlertTriangle } from 'lucide-react'

interface Props {
  target: api.Client | null  // null なら新規登録
  onClose: () => void
  onSaved: () => void
}

export default function ClientFormDialog({ target, onClose, onSaved }: Props) {
  const [name, setName] = useState(target?.name || '')
  const [baseUrl, setBaseUrl] = useState(target?.baseUrl || '')
  const [advisorToken, setAdvisorToken] = useState('')
  const [adminEmail, setAdminEmail] = useState(target?.adminEmail || '')
  const [memo, setMemo] = useState(target?.memo || '')
  const [displayOrder, setDisplayOrder] = useState(target?.displayOrder ?? 0)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  const submit = async () => {
    setSaving(true); setError('')
    try {
      if (target) {
        const payload: any = {
          id: target.id, name, baseUrl, adminEmail, memo, displayOrder,
        }
        if (advisorToken) payload.advisorToken = advisorToken
        await api.clients.update(payload)
      } else {
        if (!advisorToken) {
          setError('新規登録時はアドバイザートークンが必須です')
          setSaving(false); return
        }
        await api.clients.create({ name, baseUrl, advisorToken, adminEmail, memo, displayOrder })
      }
      onSaved()
    } catch (e) {
      setError(e instanceof Error ? e.message : '保存に失敗しました')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">
            {target ? '顧問先を編集' : '顧問先を追加'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 text-red-700 text-sm">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />{error}
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">会社名 *</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="例: SNC / TRR / NRJ"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">ベース URL *</label>
            <input type="url" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)}
              placeholder="https://cashflow-app-dzk.pages.dev"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-blue-500 outline-none" />
            <p className="text-[10px] text-gray-500 mt-1">顧問先アプリのトップページURL（末尾スラッシュなし）</p>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              アドバイザートークン {target ? '（変更時のみ入力）' : '*'}
            </label>
            <input type="text" value={advisorToken} onChange={(e) => setAdvisorToken(e.target.value)}
              placeholder={target ? '空欄なら現在のトークン維持' : '顧問先アプリの ADVISOR_TOKEN 環境変数と一致させる'}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-blue-500 outline-none" />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">SSO 用 admin メール *</label>
            <input type="email" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)}
              placeholder="cashflow 上の admin アカウントのメール"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
            <p className="text-[10px] text-gray-500 mt-1">「開く」を押した時にこのアカウントとして自動ログインします</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">並び順</label>
              <input type="number" value={displayOrder} onChange={(e) => setDisplayOrder(Number(e.target.value))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">メモ（任意）</label>
              <input type="text" value={memo} onChange={(e) => setMemo(e.target.value)}
                placeholder="補足"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 p-4 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">
            キャンセル
          </button>
          <button onClick={submit} disabled={saving}
            className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white rounded-lg">
            {saving ? '保存中...' : target ? '保存' : '追加'}
          </button>
        </div>
      </div>
    </div>
  )
}
